import { NAP } from "@/lib/nap";

// FAQ data is duplicated here (kept in sync with src/data/faq/about.js)
// so the about-page JSON-LD is self-contained and FAQ rich results
// emit independently of the React tree state.
const FAQ_PAIRS = [
  {
    q: "Who founded Siddhivinayak Group?",
    a: "Dr. Kunal G. Padole, an MBA and Ph.D. in Management, founded Siddhivinayak Infrastructure — the venture behind Siddhivinayak Group. With 16+ years of entrepreneurial leadership across real estate, education, and social sectors, he continues to drive the group's expansion across Central India.",
  },
  {
    q: "How many years of experience does Siddhivinayak Group have?",
    a: "Siddhivinayak Group has 16+ years of experience in Nagpur's real estate market, with completed and under-construction projects spanning premium apartments, plotted developments, and townships. The group has positively impacted lakhs of lives through its education and social initiatives alongside real estate.",
  },
  {
    q: "What is the relationship between Siddhivinayak Group and Kunal Padole Group?",
    a: "Siddhivinayak Group is a distinguished real-estate venture of the Kunal Padole Group. The parent group operates across real estate, education, and social initiatives, while Siddhivinayak Group focuses exclusively on residential and plotted developments in Nagpur and Central India.",
  },
  {
    q: "What is Siddhivinayak Group's mission and vision?",
    a: "The group's mission is to deliver premium residential projects that combine quality construction, transparent dealings, and long-term investment value. Its vision is to be Nagpur's most trusted real estate developer — one that contributes to the city's transformation into a world-class Smart City while maintaining the highest standards of customer trust.",
  },
  {
    q: "What awards or recognitions has Siddhivinayak Group received?",
    a: "Siddhivinayak Group has earned recognition through MahaRERA registration (P50500078967 for Hilltop Heights -14), repeat-customer referrals that drive over 40% of new sales, and consistent delivery of projects on or ahead of RERA-stipulated timelines. Customer testimonials are published on the official website and updated quarterly.",
  },
  {
    q: "How does Siddhivinayak Group ensure construction quality?",
    a: "Quality is enforced through three layers: (1) material testing at certified third-party labs, (2) on-site engineers present at every pour with stage-wise checklists, and (3) independent structural audits at slab-level milestones. The group uses branded cement, BIS-certified steel, and branded lift / electrical fittings across all residential projects.",
  },
  {
    q: "Does Siddhivinayak Group have an in-house team or outsource construction?",
    a: "Siddhivinayak Group runs an integrated model: design and project management are handled by the in-house team led by the founder, while construction is executed through a small set of long-term contractor partners who have delivered 3+ projects with the group. This balance keeps accountability in-house and execution scalable.",
  },
  {
    q: "Where is Siddhivinayak Group headquartered?",
    a: "The registered office is at No. 1/3/731, Raje Raghuji Nagar, opposite Kamala Nehru College, Nagpur 440009. Site offices are also maintained at each active project — Hilltop Heights -14, Hilltop Heights -11, Embassy-10, Siddhivinayak Nagari, and Ayodhya City — for on-ground customer service.",
  },
];

const ORG_ID = `${NAP.siteUrl}/#organization`;
const ABOUT_PAGE_ID = `${NAP.siteUrl}/about#aboutpage`;
const FAQ_ID = `${NAP.siteUrl}/about#faq`;
const LOGO_URL = `${NAP.siteUrl}/img/logo.svg`;
const OG_IMAGE = `${NAP.siteUrl}/img/og/siddhivinayak-group-og.jpg`;

const organization = {
  "@type": ["Organization", "RealEstateAgent"],
  "@id": ORG_ID,
  name: NAP.brand,
  alternateName: NAP.legalName,
  url: NAP.siteUrl,
  logo: LOGO_URL,
  description:
    "Trusted real estate developer in Nagpur (Kunal Padole Group) — premium 2 & 3 BHK flats, townships, and RERA-approved RL plots across Ram Nagar, Hanuman Nagar, Narsala, Bahadura, and Ambazari.",
  telephone: NAP.phone,
  email: NAP.email,
  foundingDate: "2009",
  founder: {
    "@type": "Person",
    name: "Dr. Kunal G. Padole",
    jobTitle: "Founder & Managing Director",
    sameAs: [],
  },
  parentOrganization: { "@type": "Organization", name: NAP.parentGroup },
  address: {
    "@type": "PostalAddress",
    streetAddress: NAP.streetAddress,
    addressLocality: NAP.addressLocality,
    addressRegion: NAP.addressRegion,
    postalCode: NAP.postalCode,
    addressCountry: NAP.addressCountry,
  },
};

const aboutPage = {
  "@type": "AboutPage",
  "@id": ABOUT_PAGE_ID,
  url: `${NAP.siteUrl}/about`,
  name: "About Siddhivinayak Group | Real Estate Nagpur",
  description:
    "Nagpur real estate developer by Kunal Padole Group — premium 2/3 BHK flats and RL plots in Ram Nagar, Hanuman Nagar, Narsala, Bahadura, Ambazari.",
  inLanguage: "en-IN",
  isPartOf: { "@id": `${NAP.siteUrl}/#website` },
  about: { "@id": ORG_ID },
  primaryImageOfPage: { "@type": "ImageObject", url: OG_IMAGE },
};

const faqPage = {
  "@type": "FAQPage",
  "@id": FAQ_ID,
  url: `${NAP.siteUrl}/about#faq`,
  isPartOf: { "@id": ABOUT_PAGE_ID },
  mainEntity: FAQ_PAIRS.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

const breadcrumb = {
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: NAP.siteUrl },
    {
      "@type": "ListItem",
      position: 2,
      name: "About",
      item: `${NAP.siteUrl}/about`,
    },
  ],
};

export const ABOUT_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [organization, aboutPage, faqPage, breadcrumb],
};